import type { Chat, Message, PendingMessage } from '@liniya/api-client';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import { MessageBubble, PendingBubble } from './MessageBubble';
import { formatDayHeading, NO_TYPING, usePresenceStore } from '@liniya/client-core';

interface Props {
  chat: Chat;
  messages: Message[];
  pending: PendingMessage[];
  viewerUserId: string;
  loading: boolean;
  hasOlder: boolean;
  loadingOlder: boolean;
  /** Whether an older page has actually been fetched for this conversation. */
  olderLoaded: boolean;
  onLoadOlder: () => void;
  onContextMenu: (message: Message, anchor: { x: number; y: number }) => void;
  /** Choosing messages to delete; the whole list becomes a set of checkboxes. */
  selecting: boolean;
  selectedIds: ReadonlySet<string>;
  canSelect: (message: Message) => boolean;
  onToggleSelected: (message: Message) => void;
  onToggleReaction: (messageId: string, emoji: string) => void;
  onRetry: (messageId: string) => void;
  lastReadSequence: number;
}

function dayKey(iso: string): string {
  return new Date(iso).toDateString();
}

/**
 * The scrolling history of one conversation.
 *
 * Two scroll behaviours matter here and both are handled explicitly rather
 * than left to the browser:
 *
 * Loading older messages prepends content above the viewport, which would
 * otherwise push what the user is reading down the screen. The scroll height
 * is measured before the change and restored after it, so the page stays
 * visually still while growing upwards.
 *
 * New messages scroll into view only when the user was already at the bottom.
 * Someone reading back through history is not interrupted by an arriving
 * message, which is the single most irritating thing a chat window can do.
 *
 * Virtualisation is deliberately not used: it fights both of these behaviours,
 * and a page of fifty messages does not need it.
 */
export function MessageList({
  chat,
  messages,
  pending,
  viewerUserId,
  loading,
  hasOlder,
  loadingOlder,
  olderLoaded,
  onLoadOlder,
  onContextMenu,
  selecting,
  selectedIds,
  canSelect,
  onToggleSelected,
  onToggleReaction,
  onRetry,
  lastReadSequence,
}: Props) {
  const container = useRef<HTMLDivElement>(null);
  const topSentinel = useRef<HTMLDivElement>(null);
  const previousHeight = useRef(0);
  const wasAtBottom = useRef(true);
  const previousChatId = useRef(chat.id);
  const typingUsers = usePresenceStore((state) => state.typingByChat[chat.id] ?? NO_TYPING);

  const atBottom = useCallback(() => {
    const node = container.current;
    if (!node) return true;
    return node.scrollHeight - node.scrollTop - node.clientHeight < 80;
  }, []);

  useLayoutEffect(() => {
    const node = container.current;
    if (!node) return;

    // A different conversation always starts at its newest message.
    if (previousChatId.current !== chat.id) {
      previousChatId.current = chat.id;
      node.scrollTop = node.scrollHeight;
      previousHeight.current = node.scrollHeight;
      wasAtBottom.current = true;
      return;
    }

    if (loadingOlder && previousHeight.current > 0) {
      // Older messages were prepended: keep the reader's position fixed by
      // absorbing exactly the height that appeared above them.
      node.scrollTop += node.scrollHeight - previousHeight.current;
    } else if (wasAtBottom.current) {
      node.scrollTop = node.scrollHeight;
    }
    previousHeight.current = node.scrollHeight;
  }, [chat.id, loadingOlder, messages, pending]);

  useEffect(() => {
    const node = container.current;
    if (!node) return;
    const onScroll = () => {
      wasAtBottom.current = atBottom();
    };
    node.addEventListener('scroll', onScroll, { passive: true });
    return () => node.removeEventListener('scroll', onScroll);
  }, [atBottom]);

  // Older pages load when the top of the list comes into view, rather than at
  // a fixed pixel offset, so it behaves the same at any zoom or font size.
  useEffect(() => {
    const sentinel = topSentinel.current;
    const root = container.current;
    if (!sentinel || !root || !hasOlder) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting) && !loadingOlder) {
          previousHeight.current = root.scrollHeight;
          onLoadOlder();
        }
      },
      { root, rootMargin: '120px 0px 0px 0px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasOlder, loadingOlder, onLoadOlder]);

  if (loading) {
    return (
      <div className="messages" aria-busy="true">
        {[0, 1, 2, 3].map((index) => (
          <div
            key={index}
            className="skeleton"
            style={{
              height: 46,
              width: index % 2 === 0 ? '48%' : '62%',
              alignSelf: index % 2 === 0 ? 'flex-start' : 'flex-end',
              borderRadius: 16,
            }}
          />
        ))}
      </div>
    );
  }

  let lastDay = '';
  let unreadMarkerPlaced = lastReadSequence <= 0;

  return (
    <div className="messages" ref={container} role="log" aria-label="История сообщений">
      <div ref={topSentinel} aria-hidden="true" />

      {loadingOlder ? (
        <div className="secure" role="status">
          Загружаем более ранние сообщения…
        </div>
      ) : null}

      {/* Only worth saying to someone who has been scrolling back: in a short
          conversation the first message is plainly the first, and a permanent
          badge above it is just noise. */}
      {!hasOlder && olderLoaded && messages.length > 0 ? (
        <div className="secure">Начало переписки</div>
      ) : null}

      {messages.length === 0 && pending.length === 0 ? (
        <div className="state-block">
          <strong>Сообщений пока нет</strong>
          <p>Напишите первое сообщение — оно появится здесь.</p>
        </div>
      ) : null}

      {messages.map((message) => {
        const key = dayKey(message.created_at);
        const showDay = key !== lastDay;
        lastDay = key;

        const showUnread =
          !unreadMarkerPlaced &&
          message.server_sequence > lastReadSequence &&
          message.sender_user_id !== viewerUserId;
        if (showUnread) unreadMarkerPlaced = true;

        return (
          <div key={message.message_id} style={{ display: 'contents' }}>
            {showDay ? <div className="date-divider">{formatDayHeading(message.created_at)}</div> : null}
            {showUnread ? <div className="unread-divider">Непрочитанные</div> : null}
            <MessageBubble
              message={message}
              own={message.sender_user_id === viewerUserId}
              channelMode={chat.kind === 'channel'}
              selected={selectedIds.has(message.message_id)}
              selecting={selecting}
              selectable={canSelect(message)}
              onToggleSelected={onToggleSelected}
              onContextMenu={onContextMenu}
              onToggleReaction={onToggleReaction}
            />
          </div>
        );
      })}

      {pending.map((message) => (
        <PendingBubble
          key={message.message_id}
          message={message}
          channelMode={chat.kind === 'channel'}
          onRetry={onRetry}
        />
      ))}

      {typingUsers.length > 0 ? (
        <div className="typing-indicator" aria-label="Собеседник печатает">
          <i />
          <i />
          <i />
        </div>
      ) : null}
    </div>
  );
}
